/* eslint-disable @typescript-eslint/camelcase, camelcase */
interface PrintParams {
    type?: 'answersOnly'|'questionsAndAnswers';
    show_pdf_panels?: boolean;
    noMargins?: boolean;
}

type AnnotationContext = 'grading' | 'ib';

export enum PreviewMode {
    QUESTION = 'question',
    QUESTION_SET = 'questionSet',
    SECTION = 'section'
}

export interface PlayerParams {
    showAnnotatableResponses?: boolean;
    assessmentRunId?: number;
    contentItemId?: number;
    context?: string;
    contentRevisionId?: number;
    contentVersionId?: number;
    dynamicLoading?: boolean;
    enableHypothesis?: boolean;
    annotationContext?: AnnotationContext;
    mode?: PreviewMode.QUESTION|PreviewMode.QUESTION_SET|PreviewMode.SECTION;
    noMargins?: boolean;
    partyId?: number;
    path?: string;
    printParams?: PrintParams;
    layoutParams?: any;
    selectedLanguageId?: string;
    annotationSidebarDefaultExpanded?: boolean;
    gradingKeyboardNavigationSupport?: boolean;
    scorableItem?: number;
    sectionId?: number;
    isSpeakingContext?: boolean;
    viewAs?: 'candidate';
    useBestMatchThresholdForQuote?: boolean;
    useBestMatchLogging?: boolean;
    removeFrameNumber?: boolean;
    isPreviewMode?: boolean;
    isReviewMode?: boolean;
    showAudioScripts?: boolean;
    viewMedia?: string;
    sectionStimulusRevisionID?: number;
    previewContext?: string;
}

export type Dict = {
    key: string;
    value: any;
};

function appendValue(accumulator: PlayerParams, item: Dict): PlayerParams {
    (accumulator as any)[item.key] = item.value;
    return accumulator;
}

class PlayerRequestBuilder {
    private params: PlayerParams;

    baseUrl = (): string => '/player/index.jsp';

    static create(): PlayerRequestBuilder {
        return new PlayerRequestBuilder();
    }

    constructor() {
        this.params = {};
    }

    annotationContext(context: AnnotationContext): this {
        return this.setParams({ annotationContext: context });
    }

    showAnnotatableResponses(weNeedIt = true): this {
        return this.setParams({ showAnnotatableResponses: weNeedIt });
    }

    assessmentRunId(assessmentRunId: number): this {
        return this.setParams({ assessmentRunId });
    }

    contentRevisionId(contentRevisionId: number): this {
        return this.setParams({ contentRevisionId });
    }

    contentVersionId(contentVersionId: number): this {
        return this.setParams({ contentVersionId });
    }

    contentItemId(contentItemId: number): this {
        return this.setParams({ contentItemId });
    }

    scorableItem(scorableItem: number): this {
        return this.setParams({ scorableItem });
    }

    sectionId(sectionId: number): this {
        return this.setParams({ sectionId });
    }

    viewAsCandidate(): this {
        return this.setParams({ viewAs: 'candidate' });
    }

    context(context: string): this {
        return this.setParams({ context });
    }

    dynamicLoading(dynamicLoading = true): this {
        return this.setParams({ dynamicLoading });
    }

    previewMode(isPreviewMode = false): this {
        return this.setParams({ isPreviewMode });
    }

    reviewMode(isReviewMode = false): this {
        return this.setParams({ isReviewMode });
    }

    isSpeakingContext(isSpeakingContext = true): this {
        return this.setParams({ isSpeakingContext });
    }

    gradingKeyboardNavigationSupport(gradingKeyboardNavigationSupport = true): this {
        return this.setParams({ gradingKeyboardNavigationSupport });
    }

    removeFrameNumber(removeFrameNumber = true): this {
        return this.setParams({ removeFrameNumber });
    }

    enableAnnotations(enableHypothesis = true): this {
        const bestMatchThresholdParams: PlayerParams = {};
        if (enableHypothesis && window.marketplaceProperties?.ENABLE_HYPOTHESIS_BEST_MATCH_THRESHOLD) {
            bestMatchThresholdParams.useBestMatchThresholdForQuote = true;
            if (!['prod', 'stage'].includes(window.deployEnvironment)) {
                bestMatchThresholdParams.useBestMatchLogging = true;
            }
        }
        return this.setParams({ enableHypothesis, ...bestMatchThresholdParams });
    }

    mode(mode: PlayerParams['mode']): this {
        return this.setParams({ mode });
    }

    navigateToQuestion(questionId: number): this {
        return this.setParams({ path: `/question/${questionId}` });
    }

    noMargins(noMargins = true): this {
        return this.setPrintParams({ noMargins });
    }

    annotationSidebarDefaultExpanded(annotationSidebarDefaultExpanded = false): this {
        return this.setParams({ annotationSidebarDefaultExpanded });
    }

    partyId(partyId: number): this {
        return this.setParams({ partyId });
    }

    path(path: string): this {
        return this.setParams({ path });
    }

    printParams(printParams: any): this {
        return this.setParams({ printParams });
    }

    layoutParams(layoutParams: any): this {
        return this.setParams({ layoutParams });
    }

    selectedLanguageId(selectedLanguageId: string): this {
        return this.setParams({ selectedLanguageId });
    }

    showPdfPanels(show_pdf_panels = true): this {
        return this.setPrintParams({ show_pdf_panels });
    }

    showQuestionText(showQuestionText = true): this {
        return this.setPrintParams({ type: showQuestionText ? 'questionsAndAnswers' : 'answersOnly' });
    }

    showAudioScripts(showAudioScripts: boolean): this {
        return this.setParams({ showAudioScripts });
    }

    stimulusContentRevisionId(sectionStimulusRevisionID: number): this {
        return this.setParams({ sectionStimulusRevisionID });
    }

    setPreviewContext(previewContext?: string): this {
        return previewContext ? this.setParams({ previewContext }) : this;
    }

    setParams(newParams: PlayerParams): this {
        this.params = { ...this.params, ...newParams };
        return this;
    }

    setPrintParams(newParams: PrintParams): this {
        const oldParams = this.params.printParams || {};
        return this.setParams({ printParams: { ...oldParams, ...newParams } });
    }

    asKeyValue(): Dict[] {
        return Object.keys(this.params)
            .map((key) => ({ key, value: (this.params as any)[key] }))
            .filter((entry) => entry.value !== undefined);
    }

    toMessageParams(): PrintParams {
        return this.asKeyValue()
            .filter((item) => item.value !== undefined)
            .reduce(appendValue, {});
    }

    toUrl(): string {
        const params = this.asKeyValue()
            .map((item) => {
                if (typeof item.value === 'object') {
                    item.value = JSON.stringify(item.value);
                    if (item.key === 'layoutParams') {
                        item.value = encodeURIComponent(item.value);
                    }
                }
                return item;
            })
            .map((item) => `${item.key}=${item.value}`)
            .join('&');

        return `${this.baseUrl()}?${params}`;
    }

    removeParam(key: keyof PlayerParams): this {
        if (key in this.params) {
            const newParams = { ...this.params };
            delete newParams[key];
            this.params = newParams;
        }
        return this;
    }
}

export default PlayerRequestBuilder;
